import { generateUUID } from 'util/misc'
import { autoFetchDlubalDataIfNeeded } from '../../../dlubal'
import { BomGeneratorAbstract } from '../../bom_generators/BomGeneratorAbstract'
import { getContiguousRows } from '../../bom_generators/PerRowBomGeneratorAbstract'
import {
  BasicSpacingLayoutAdjusterAbstract,
  SpacingRuleSet,
} from '../../layout_adjusters/BasicSpacingLayoutAdjusterAbstract'
import { getMlpeQuantityForBlocks, getSystemFromBlocks } from '../../mlpeBlockUtils'
import { MountingSystemAbstract } from '../../MountingSystemAbstract'
import { CompatibilityParameters, MountingCalcResult, Panel } from '../../types'
import { getPZSECache } from './PZSE'
import { trackSnapnrackBomSuccess, trackSnapnrackRackingError } from './snapnrackAmplitude'
import { getAttachmentOptionsForRoofAndStyle, getDefaultAttachmentCode } from './UltraRailAttachments'
import { ULTRA_RAIL_COMPATIBILITY_PARAMS } from './UltraRailCompatibility'
import { getFastenersForAttachment } from './UltraRailFasteners'
import {
  getRailLengthInchesForRailTypeCode as getRailLengthInches,
  getProfileFromRailTypeCode as getRailProfile,
  getSpliceCodeForFinish,
} from './UltraRailInputOptions'
import { computeTotalRailsAndSplices } from './UltraRailSplicing'
import type { UltraRailMountingType, UltraRailRailType } from './UltraRailTypes'
import { computeBottomRowLengthMmForSkirt, computeSkirtRailBomItems } from './UniversalSkirtRail'

const ULTRA_RAIL_SPACING_MM = 19.05
const DEFAULT_ATTACHMENT_SPACING_INCHES = 48
const UNIVERSAL_END_CLAMP_LENGTH_MM = 25.4
const MM_PER_INCH = 25.4

// Default rail piece length 172 in.
const DEFAULT_RAIL_LENGTH_INCHES = 172

const DEFAULT_END_CLAMP_CODE = '242-02215'
const DEFAULT_MID_CLAMP_CODE = '242-02071'

function resolveAttachmentCode(
  storedAttachment: string | undefined,
  attachmentOptions: { code: string }[],
  roofType: string | null | undefined
): string | null {
  if (storedAttachment && attachmentOptions.some((o) => o.code === storedAttachment)) {
    return storedAttachment
  }
  const defaultAttachment = getDefaultAttachmentCode(roofType as Parameters<typeof getDefaultAttachmentCode>[0])
  if (defaultAttachment && attachmentOptions.some((o) => o.code === defaultAttachment)) {
    return defaultAttachment
  }
  return attachmentOptions[0]?.code ?? null
}

function bomItems(code: string, count: number) {
  return Array.from({ length: count }, () => ({ name: code, components: [] }))
}

const spacingRuleSet: SpacingRuleSet = {
  interPanel: { horizontalSpacing: ULTRA_RAIL_SPACING_MM, verticalSpacing: ULTRA_RAIL_SPACING_MM },
}

class SnapNRackUltraRailLayoutAdjuster extends BasicSpacingLayoutAdjusterAbstract {
  spacingRuleSet = spacingRuleSet
}

const latestGenerationIdBySystem = new Map<string, string>()

class SnapNRackUltraRailBomGenerator extends BomGeneratorAbstract {
  async generateBom(result: MountingCalcResult): Promise<MountingCalcResult> {
    const systemUuid = window.editor?.selectedSystem?.uuid
    const pzseCache = getPZSECache(systemUuid)
    if (pzseCache?.success && pzseCache.value != null) {
      const spacing = Number(this.input.options?.attachmentSpacingInches ?? DEFAULT_ATTACHMENT_SPACING_INCHES)
      if (spacing > pzseCache.value) {
        trackSnapnrackRackingError(
          'attachment_spacing_above_pzse_max',
          `Attachment spacing ${spacing} in exceeds PZSE max ${pzseCache.value} in`,
          {
            systemId: systemUuid,
            attachment_spacing_inches: spacing,
            pzse_max_span_inches: pzseCache.value,
          }
        )
        return result
      }
    }

    // Ensure that the BOM generation is not duplicated if user toggles dropdown while waiting for Dlubal
    const blockId = this.block.uuid
    const generationId = generateUUID()
    latestGenerationIdBySystem.set(blockId, generationId)

    await autoFetchDlubalDataIfNeeded(this.input.options)

    // guard against duplicate calls
    if (latestGenerationIdBySystem.get(blockId) !== generationId) {
      return result
    }

    const roofType = this.input.roof.roofTypeName
    const mountingType = (this.input.options?.mountingType ?? 'Rafter') as UltraRailMountingType
    const opts = this.input.options ?? {}
    const attachmentOptions = roofType ? getAttachmentOptionsForRoofAndStyle(roofType, mountingType) : []
    const storedAttachment = opts.attachment as string | undefined
    const attachmentCode = resolveAttachmentCode(storedAttachment, attachmentOptions, roofType)
    if (attachmentCode) {
      const railTypeCode = (opts.railType as string) || '232-10096'
      const railLengthInches = getRailLengthInches(railTypeCode) ?? DEFAULT_RAIL_LENGTH_INCHES
      const railType = getRailProfile(railTypeCode) ?? ('UR-45' as UltraRailRailType)

      const endClampCode = (opts.endClamp as string) || DEFAULT_END_CLAMP_CODE
      const midClampCode = (opts.midClamp as string) || DEFAULT_MID_CLAMP_CODE

      const fastenersForAttachment = getFastenersForAttachment(attachmentCode, mountingType)
      const selectedFastener = opts.fastener as string | undefined
      const validFastenerMatch = selectedFastener && fastenersForAttachment.find((s) => s.code === selectedFastener)
      const fasteners = validFastenerMatch
        ? [validFastenerMatch]
        : fastenersForAttachment[0]
          ? [fastenersForAttachment[0]]
          : []

      const railRun = this.getRailRun()
      const rowLengthsMm = this.computeRowLengthsMm()

      const totalAttachments = this.computeTotalRoofAttachmentsForBlock(railRun, rowLengthsMm)
      const attachmentItems = bomItems(attachmentCode, totalAttachments)
      const fastenerItems = fasteners.flatMap((spec) => bomItems(spec.code, totalAttachments * spec.qtyPerAttachment))

      const railLengthMm = railLengthInches * MM_PER_INCH

      const { totalPieces, totalSplices } = this.computeTotalRailsAndSplices(railLengthMm, railRun, rowLengthsMm)
      const railItems = bomItems(railTypeCode, totalPieces)

      const midClampItems = bomItems(midClampCode, this.computeTotalMidClampsForBlock(railRun))
      const endClampItems = bomItems(endClampCode, this.computeTotalEndClampsForBlock(railRun))

      const activeRailSegments = this.getActiveContiguousRowSegments()
      const endCapItems =
        railType === 'UR-45' ? bomItems('232-10101', 2 * railRun * activeRailSegments.length) : []

      const spliceCode = getSpliceCodeForFinish(railType, (opts.spliceFinish as string) || '')
      const spliceItems = spliceCode && totalSplices > 0 ? bomItems(spliceCode, totalSplices) : []
      const cableManagementItems = this.computeCableManagementItems()

      result.items = [
        ...attachmentItems,
        ...fastenerItems,
        ...railItems,
        ...spliceItems,
        ...midClampItems,
        ...endClampItems,
        ...endCapItems,
        ...cableManagementItems,
      ]
    } else if (roofType) {
      trackSnapnrackRackingError(
        'no_valid_attachment',
        'No valid attachment resolved for roof type and mounting style',
        {
          systemId: systemUuid,
          roofType,
          mountingType,
        }
      )
    }

    // Skirt rail BOM: only for layout's bottom-most row, when Universal Skirt Rail is selected
    const universalSkirtRail = opts.universalSkirtRail as string | undefined
    if (universalSkirtRail && universalSkirtRail !== 'None') {
      const bottomBlockIndex = result.panelBlocks.length - 1
      if (this.blockIndex === bottomBlockIndex) {
        const bottomRowLengthMm = computeBottomRowLengthMmForSkirt(this.block)
        const skirtItems = computeSkirtRailBomItems({
          bottomRowLengthMm,
          designSnowLoad: opts.designSnowLoad as number | undefined,
          skirtRailSpacer: opts.skirtRailSpacer as string | undefined,
        })
        result.items = [...result.items, ...skirtItems]
      }
    }

    if (result.items.length > 0) {
      trackSnapnrackBomSuccess({
        result,
        systemId: systemUuid,
        panelCount: this.countActivePanels(),
        orientation: this.block.orientation,
        roofType,
        options: {
          railRun: opts.railRun as string | undefined,
          railType: opts.railType as string | undefined,
          mountingType: opts.mountingType as string | undefined,
          attachment: opts.attachment as string | undefined,
          attachmentSpacingInches: opts.attachmentSpacingInches as string | number | undefined,
          universalSkirtRail: opts.universalSkirtRail as string | undefined,
          cableManagers: opts.cableManagers as string | undefined,
        },
        pzseMaxSpanInches: pzseCache?.value ?? null,
        pzseCacheSuccess: pzseCache?.success ?? null,
        mlpeCount: this.safeGetMLPECount(),
      })
    }

    return result
  }

  private getRailRun(): number {
    const railRun = (this.input.options?.railRun ?? '2 Rail') as string
    return railRun === '3 Rail' ? 3 : 2
  }

  /** Active panel runs per grid row (gaps between inactive slots split into separate segments). */
  private getActiveContiguousRowSegments(): Panel[][] {
    return this.block.rows.flatMap((row) => getContiguousRows(row))
  }

  private computeRowLengthsMm(): number[] {
    const panelDimensionMm = this.block.orientation === 'portrait' ? this.block.panel.width : this.block.panel.height
    const universalEndClampCodes = ['242-02215', '242-02215-USA']
    const endClampCode = this.input.options?.endClamp as string
    const endClampLengthMm = universalEndClampCodes?.includes(endClampCode) ? 0 : 2 * UNIVERSAL_END_CLAMP_LENGTH_MM

    return this.getActiveContiguousRowSegments().map((segment) => {
      const activeCount = segment.length
      const panelLength = activeCount * panelDimensionMm
      const gapLength = (activeCount - 1) * ULTRA_RAIL_SPACING_MM
      return panelLength + gapLength + endClampLengthMm
    })
  }

  /** Delegates to shared computeTotalRailsAndSplices (used by BOM and tests). */
  private computeTotalRailsAndSplices(
    railLengthMm: number,
    railRun: number,
    rowLengthsMm: number[]
  ): { totalPieces: number; totalSplices: number } {
    return computeTotalRailsAndSplices(railLengthMm, railRun, rowLengthsMm)
  }

  /**
   * Number of roof attachments per rail length = ((length of row - 2 * Overhang) / Span) + 1;
   * Overhang ≤ max span/3; total = attachments per rail * number of rail lengths (2 or 3) per row, summed over rows.
   */
  private computeTotalRoofAttachmentsForBlock(railRun: number, rowLengthsMm: number[]): number {
    const spanInches = (this.input.options?.attachmentSpacingInches ?? DEFAULT_ATTACHMENT_SPACING_INCHES) as number
    const spanMm = spanInches * MM_PER_INCH
    const overhangMm = spanMm / 3

    let total = 0
    for (const rowLengthMm of rowLengthsMm) {
      const totalOverhangMm = 2 * overhangMm
      const attachmentSectionLengthMm = rowLengthMm - totalOverhangMm
      const attachmentsPerRail = Math.max(2, Math.floor(attachmentSectionLengthMm / spanMm) + 1)
      total += attachmentsPerRail * railRun
    }
    return total
  }

  private computeTotalEndClampsForBlock(railRun: number): number {
    return railRun * this.getActiveContiguousRowSegments().length * 2
  }

  private computeTotalMidClampsForBlock(railRun: number): number {
    return (
      railRun *
      this.getActiveContiguousRowSegments().reduce((sum, segment) => sum + Math.max(0, segment.length - 1), 0)
    )
  }

  private countActivePanels(): number {
    return this.block.rows.flatMap((row) => row).filter((panel) => panel.isActive).length
  }

  private countRowsWithActivePanels(): number {
    return this.block.rows.filter((row) => row.some((panel) => panel.isActive)).length
  }

  private computeCableManagementItems() {
    if (this.input.options?.cableManagers !== 'Yes') return []

    const totalRows = this.countRowsWithActivePanels()
    const totalPanels = this.countActivePanels()
    const mlpeCount = this.getMLPECount()
    const omniLugCount = totalRows + mlpeCount

    return [
      ...bomItems('242-02150', totalRows),
      ...bomItems('232-01106', totalPanels * 3),
      ...bomItems('242-10034-USA', omniLugCount),
    ]
  }

  private getMLPECount(): number {
    const system = getSystemFromBlocks([this.block])
    return getMlpeQuantityForBlocks([this.block], system)
  }

  private safeGetMLPECount(): number {
    try {
      return this.getMLPECount()
    } catch {
      return 0
    }
  }
}

export class SnapNRackUltraRail extends MountingSystemAbstract {
  layoutAdjusterClass = SnapNRackUltraRailLayoutAdjuster
  bomGeneratorClass = SnapNRackUltraRailBomGenerator

  getCompatibilityParameters(): CompatibilityParameters {
    return ULTRA_RAIL_COMPATIBILITY_PARAMS
  }
}
